/**
 * usePillars — real-time subscription to projects/{pid}/topic_pillars/.
 *
 * Used by Topic Backlog UI + TopicDetailPanel to resolve pillar.id →
 * pillar.name when rendering Phase B `pillarIds` / `primaryPillarId` chips.
 *
 * Schema: `TopicPillar` is not yet in @segmently/schema-models (Phase B
 * canonical bump is pending). This hook returns a structurally-typed shape
 * matching what AuthorOnboardingHandler writes today; tighten the type when
 * the schema lands.
 */

import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

export interface TopicPillarLite {
  id: string;
  name: string;
  description?: string;
  summary?: string;
  knowledgeContext?: string;
  keyTheses?: Array<{
    thesis: string;
    confidence?: number;
    sourcePostIds?: string[];
  }>;
  sourcePostIds?: string[];
  sourceHighlights?: Array<{
    postId: string;
    platformId?: string;
    quoteOrParaphrase: string;
    relevance: string;
  }>;
  guardrails?: string[];
  weight?: number;
  funnelStage?: 'TOFU' | 'MOFU' | 'BOFU' | string;
  authorStance?: string;
  authorIds?: string[];
  isDraft?: boolean;
}

export interface UsePillarsReturn {
  pillars: TopicPillarLite[];
  byId: Record<string, TopicPillarLite>;
  loading: boolean;
  error: Error | null;
}

export function usePillars(projectId: string | null): UsePillarsReturn {
  const [pillars, setPillars] = useState<TopicPillarLite[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setPillars([]);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const q = query(collection(db, 'projects', projectId, 'topic_pillars'));
    setLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPillars(
          snap.docs.map((d) => {
            const data = d.data() as Partial<TopicPillarLite>;
            return { ...(data as TopicPillarLite), id: d.id };
          }),
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId]);

  const byId = useMemo(() => {
    const m: Record<string, TopicPillarLite> = {};
    for (const p of pillars) m[p.id] = p;
    return m;
  }, [pillars]);

  return { pillars, byId, loading, error };
}
